/* ============================================================
 * Korea store opening landing page
 * Custom element: <kr-store-opening>
 *
 * Handles client-side validation and the default / error / success
 * states for the entry ("응모하기") form, plus screen-reader
 * announcements via a live region.
 *
 * Submits to the backend "API endpoint" setting as JSON. The backend
 * only accepts requests from https://www.brochuwalker.com, so the real
 * POST runs on the production storefront; the theme editor preview (and
 * a blank endpoint) fall back to a client-side mock success.
 * ============================================================ */
(function () {
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Korean mobile numbers: 010 / 011 / 016 / 017 / 018 / 019, 10–11 digits,
  // with an optional +82 country code and optional spaces / hyphens / parens.
  const KR_MOBILE_RE = /^01[0167-9]\d{7,8}$/;
  const isKoreanMobile = (raw) => {
    let v = (raw || '').replace(/[\s.\-()]/g, ''); // drop separators
    v = v.replace(/^\+?82/, '0'); // +82 / 82 country code -> domestic leading 0
    v = v.replace(/^00/, '0'); // collapse "+82 0..." into a single leading 0
    return KR_MOBILE_RE.test(v);
  };

  const onlyDigits = (s) => (s.match(/\d/g) || []).join('');

  // Live input mask -> "010-1234-5678" (domestic) or "+82 10-1234-5678" (intl).
  const formatKoreanPhone = (raw) => {
    if ((raw || '').trimStart().startsWith('+')) {
      const d = onlyDigits(raw);
      if (!d.startsWith('82')) return '+' + d.slice(0, 2);
      const rest = d.slice(2).replace(/^0/, '').slice(0, 10); // operator(2)+4+4
      let out = '+82';
      if (rest.length) out += ' ' + rest.slice(0, 2);
      if (rest.length > 2) out += '-' + rest.slice(2, 6);
      if (rest.length > 6) out += '-' + rest.slice(6, 10);
      return out;
    }
    const d = onlyDigits(raw).slice(0, 11); // 3+4+4
    let out = d.slice(0, 3);
    if (d.length > 3) out += '-' + d.slice(3, 7);
    if (d.length > 7) out += '-' + d.slice(7, 11);
    return out;
  };

  // Canonical E.164 ("+8210XXXXXXXX") for the API payload.
  const toE164KR = (raw) => {
    const v = (raw || '')
      .replace(/[\s.\-()]/g, '')
      .replace(/^\+?82/, '0')
      .replace(/^00/, '0');
    return KR_MOBILE_RE.test(v) ? '+82' + v.slice(1) : (raw || '').trim();
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const inThemeEditor = () =>
    typeof window.Shopify !== 'undefined' && window.Shopify.designMode === true;

  const scrollTo = (el) => {
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    }
  };

  class KrStoreOpening extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-kr-form]');
      if (!this.form || this._bound) return;
      this._bound = true;

      this.hint = this.querySelector('[data-kr-hint]');
      this.live = this.querySelector('[data-kr-live]');
      this.networkError = this.querySelector('[data-kr-network-error]');
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.defaultHintText = this.hint ? this.hint.textContent.trim() : '';

      this._onSubmit = this.onSubmit.bind(this);
      this._onInput = this.onInput.bind(this);
      this.form.addEventListener('submit', this._onSubmit);
      this.form.addEventListener('input', this._onInput);
      this.form.addEventListener('change', this._onInput);
    }

    disconnectedCallback() {
      if (!this.form) return;
      this.form.removeEventListener('submit', this._onSubmit);
      this.form.removeEventListener('input', this._onInput);
      this.form.removeEventListener('change', this._onInput);
      this._bound = false;
    }

    /** Fields that must pass validation (inputs + the required consent box). */
    get requiredFields() {
      return Array.from(this.form.querySelectorAll('[data-kr-required]'));
    }

    isValid(el) {
      if (el.type === 'checkbox') return el.checked;
      const value = (el.value || '').trim();
      if (!value) return false;
      if (el.dataset.krType === 'email') return EMAIL_RE.test(value);
      if (el.dataset.krType === 'phone') return isKoreanMobile(value);
      return true;
    }

    isEmpty(el) {
      return el.type === 'checkbox' ? !el.checked : (el.value || '').trim() === '';
    }

    allValid() {
      return this.requiredFields.every((el) => this.isValid(el));
    }

    markField(el, valid) {
      el.classList.toggle('is-invalid', !valid);
      el.setAttribute('aria-invalid', valid ? 'false' : 'true');
    }

    setHint(text) {
      if (this.hint && typeof text === 'string') this.hint.textContent = text;
    }

    /** Live-format the phone field, preserving the caret by digit count. */
    formatPhoneField(el) {
      const caret = el.selectionStart == null ? el.value.length : el.selectionStart;
      const digitsBefore = onlyDigits(el.value.slice(0, caret)).length;
      const formatted = formatKoreanPhone(el.value);
      if (formatted === el.value) return;
      el.value = formatted;
      let pos = 0;
      let seen = 0;
      while (pos < formatted.length && seen < digitsBefore) {
        if (/\d/.test(formatted[pos])) seen += 1;
        pos += 1;
      }
      try {
        el.setSelectionRange(pos, pos);
      } catch (e) {
        /* setSelectionRange unsupported on this input type — ignore */
      }
    }

    /** Announce a message via the live region (assertive for errors). */
    announce(message, assertive) {
      if (!this.live || !message) return;
      this.live.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
      this.live.textContent = '';
      // Re-set on the next frame so the region fires even for repeated text.
      window.requestAnimationFrame(() => {
        this.live.textContent = message;
      });
    }

    hideNetworkError() {
      if (this.networkError) this.networkError.hidden = true;
    }

    onInput(event) {
      const el = event.target;
      // Phone field: live-format to 010-1234-5678 / +82 10-1234-5678.
      if (el.dataset && el.dataset.krType === 'phone') {
        this.formatPhoneField(el);
      }
      if (el.matches('[data-kr-required]') && this.isValid(el)) {
        this.markField(el, true);
      }
      // Once everything passes again, restore the default hint.
      if (this.getAttribute('data-state') === 'error' && this.allValid()) {
        this.setAttribute('data-state', 'default');
        this.setHint(this.defaultHintText);
      }
    }

    onSubmit(event) {
      event.preventDefault();
      if (this._busy) return;
      this.hideNetworkError();

      const required = this.requiredFields;
      required.forEach((el) => this.markField(el, this.isValid(el)));
      const invalid = required.filter((el) => !this.isValid(el));

      if (invalid.length) {
        const hasEmpty = required.some((el) => this.isEmpty(el));
        // Empty fields -> generic "fill required" hint. Otherwise the failure
        // is a format error (phone/email) -> show that field's own message.
        const formatBad = invalid.find((el) => !this.isEmpty(el) && el.dataset.krError);
        const message = hasEmpty || !formatBad ? this.defaultHintText : formatBad.dataset.krError;
        const focusEl = !hasEmpty && formatBad ? formatBad : invalid[0];

        this.setAttribute('data-state', 'error');
        this.setHint(message);
        this.announce(message, true);
        if (focusEl && typeof focusEl.focus === 'function') focusEl.focus();
        return;
      }

      this.submitForm();
    }

    setBusy(busy) {
      this._busy = busy;
      if (this.submitBtn) {
        this.submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
        this.submitBtn.disabled = busy;
      }
    }

    submitForm() {
      const payload = this.buildPayload();
      const endpoint = this.dataset.endpoint;
      this.setBusy(true);

      // Real submission only to a trusted https endpoint, and never from the
      // theme editor preview (keeps test PII out of the backend).
      if (endpoint && /^https:\/\//i.test(endpoint) && !inThemeEditor()) {
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        })
          .then((res) => {
            if (!res.ok) throw new Error('Request failed: ' + res.status);
            return res.json().catch(() => ({}));
          })
          .then(() => this.showSuccess())
          .catch((err) => this.showSubmitError(err))
          .finally(() => this.setBusy(false));
        return;
      }

      // ---- CLIENT-ONLY MOCK (no backend yet, or editor preview) ----
      window.setTimeout(() => {
        this.setBusy(false);
        this.showSuccess();
      }, 400);
    }

    /**
     * Build the request body in the backend's contract. Maps form field
     * names to API keys and always emits both consent flags as booleans:
     *   name -> name, phone -> mobileNumber, email -> email,
     *   instagram -> instagramLink, consent_required -> piConsent,
     *   consent_marketing -> marketingConsent
     */
    buildPayload() {
      const field = (name) => this.form.querySelector('[name="' + name + '"]');
      const text = (name) => {
        const el = field(name);
        return el ? (el.value || '').trim() : '';
      };
      const bool = (name) => {
        const el = field(name);
        return !!(el && el.checked);
      };
      return {
        name: text('name'),
        mobileNumber: toE164KR(text('phone')),
        email: text('email'),
        instagramLink: text('instagram'),
        piConsent: bool('consent_required'),
        marketingConsent: bool('consent_marketing'),
      };
    }

    showSuccess() {
      this.hideNetworkError();
      this.setAttribute('data-state', 'success');

      const heading = this.querySelector('[data-kr-success-heading]');
      const body = this.querySelector('[data-kr-success-body]');
      const message = (body && body.textContent.trim()) || (heading && heading.textContent.trim());

      if (heading) {
        heading.focus({ preventScroll: true });
        scrollTo(heading);
      }
      this.announce(message, false);
    }

    showSubmitError(err) {
      // eslint-disable-next-line no-console
      if (err) console.error('[korea-store-opening] submit failed', err);
      // Keep the form visible for retry and show a DISTINCT network-error
      // message — not the required-fields hint, which would be misleading.
      if (this.networkError) {
        this.networkError.hidden = false;
        this.announce(this.networkError.textContent.trim(), true);
        scrollTo(this.networkError);
      } else {
        this.announce('잠시 후 다시 시도해 주세요.', true);
      }
    }
  }

  if (!customElements.get('kr-store-opening')) {
    customElements.define('kr-store-opening', KrStoreOpening);
  }
})();
